import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../supabaseClient';

/** sessionStorage key; set/cleared from AuthProvider onAuthStateChange */
export const SESSION_ANCHOR_KEY = 'daypop_session_started_ms';

/**
 * Parse timeout ms from env. "0" = feature disabled (useful in dev).
 * CRA inlines at build time — restart dev server after changing .env.
 */
function parseOptionalMs(envName, defaultMs) {
  const v = process.env[envName];
  if (v === '0') return 0;
  if (v === undefined || v === '') return defaultMs;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : defaultMs;
}

const IDLE_MS = parseOptionalMs('REACT_APP_SESSION_IDLE_MS', 90 * 60 * 1000);
const MAX_MS = parseOptionalMs('REACT_APP_SESSION_MAX_MS', 2 * 60 * 60 * 1000);
const CHECK_INTERVAL_MS = 30_000;

/**
 * Signs out after idle inactivity and/or max wall-clock session length (from last sign-in / tab anchor).
 */
export function useSessionTimeout(session) {
  const navigate = useNavigate();
  const lastActivityRef = useRef(Date.now());
  const userId = session?.user?.id;

  const performLogout = useCallback(async (message) => {
    try {
      sessionStorage.removeItem(SESSION_ANCHOR_KEY);
      await supabase.auth.signOut();
    } catch (e) {
      console.warn('[Daypop] session timeout signOut:', e);
      sessionStorage.removeItem(SESSION_ANCHOR_KEY);
    }
    toast.info(message, { duration: 6000 });
    navigate('/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!userId) {
      return undefined;
    }

    if (MAX_MS > 0 && !sessionStorage.getItem(SESSION_ANCHOR_KEY)) {
      sessionStorage.setItem(SESSION_ANCHOR_KEY, String(Date.now()));
    }

    const bumpActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const opts = { capture: true, passive: true };
    events.forEach((ev) => window.addEventListener(ev, bumpActivity, opts));

    bumpActivity();

    const tick = () => {
      const now = Date.now();

      if (IDLE_MS > 0 && now - lastActivityRef.current > IDLE_MS) {
        void performLogout('You were signed out after a period of inactivity.');
        return;
      }

      if (MAX_MS > 0) {
        const raw = sessionStorage.getItem(SESSION_ANCHOR_KEY);
        const started = raw ? Number(raw) : NaN;
        if (Number.isFinite(started) && now - started > MAX_MS) {
          void performLogout('Your session has expired. Please sign in again.');
        }
      }
    };

    const interval = window.setInterval(tick, CHECK_INTERVAL_MS);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bumpActivity, opts));
      window.clearInterval(interval);
    };
  // Note: do NOT include accessToken — Supabase refreshes it periodically,
  // which would restart this effect and reset the idle timer to zero.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, performLogout]);
}
